import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { type FC, useContext } from "react";
import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu";
import "react-horizontal-scrolling-menu/dist/styles.css";
import { useObjectiveProgressOverview } from "../../data";
import { useObjectiveProgressOverviewTranslations } from "./i18n";
import ThemeProgressCard from "./ThemeProgressCard";
import { ThemeProgressCardSkeleton } from "./ThemeProgressCard/ThemeProgressCardSkeleton";

type Props = { entityId: string };

export const ObjectiveProgressOverview: FC<Props> = ({ entityId }) => {
    const { data, isPending } = useObjectiveProgressOverview(entityId);
    const { t, keys } = useObjectiveProgressOverviewTranslations();

    return (
        <Box mx="4" py="4" className="border rounded-xl">
            <FlexRow justifyContent="between" alignItems="center" mb="4" px="4">
                <Text variant="header3">{t(keys.title)}</Text>
            </FlexRow>

            <Box className="relative">
                {/* Scroll container */}
                <ScrollMenu
                    LeftArrow={LeftArrow}
                    RightArrow={RightArrow}
                    scrollContainerClassName="flex"
                >
                    {isPending
                        ? Array.from({ length: 3 }).map((_, idx) => (
                              <ThemeProgressCardSkeleton key={idx} />
                          ))
                        : [
                              // Start spacer using Box
                              <Box
                                  key="start-spacer"
                                  w="1"
                                  p="2"
                                  data-spacer
                              />,
                              // Mapped items
                              ...(data?.objectiveProgressOverview || []).map(
                                  (objectiveProgressOverview, index, array) => (
                                      <ThemeProgressCard
                                          itemId={
                                              objectiveProgressOverview.themeId
                                          }
                                          key={
                                              objectiveProgressOverview.themeId
                                          }
                                          theme={objectiveProgressOverview}
                                          className={cn(
                                              "flex-shrink-0",
                                              // Add right margin to all except the last item
                                              index < array.length - 1 &&
                                                  "mr-7",
                                          )}
                                      />
                                  ),
                              ),
                              // End spacer using Box
                              <Box
                                  key="end-spacer"
                                  w="1"
                                  p="2"
                                  className="flex-shrink-0"
                                  data-spacer
                              />,
                          ]}
                </ScrollMenu>
            </Box>
        </Box>
    );
};

const LeftArrow = () => {
    const visibility = useContext(VisibilityContext);
    const isFirstItemVisible = visibility.useIsVisible("first", true);

    if (isFirstItemVisible) {
        return null;
    }

    return (
        <Button
            variant="outlined"
            size="sm"
            onClick={() => visibility.scrollPrev()}
            className={cn(
                "absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full hover:shadow-xl transition-shadow",
                isFirstItemVisible && "hidden",
            )}
            aria-label="Scroll left"
        >
            <Button.Icon name="chevronLeft" />
        </Button>
    );
};

const RightArrow = () => {
    const visibility = useContext(VisibilityContext);
    const isLastItemVisible = visibility.useIsVisible("last", false);

    return (
        <Button
            variant="outlined"
            size="sm"
            onClick={() => visibility.scrollNext()}
            className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full hover:shadow-xl transition-shadow",
                isLastItemVisible && "hidden",
            )}
            aria-label="Scroll right"
        >
            <Button.Icon name="chevronRight" />
        </Button>
    );
};
